<template>
  <teleport to="body">
    <transition name="offline-modal-fade">
      <div
        v-if="isVisible"
        class="offline-backdrop"
        @click.self="dismiss"
        @keydown.escape="dismiss"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-title"
      >
        <transition name="offline-sheet-slide">
          <div
            v-if="isVisible"
            class="offline-bottom-sheet"
            tabindex="-1"
            aria-describedby="offline-desc"
          >
            <!-- Body Content -->
            <div class="offline-sheet-body">
              <div class="offline-icon-wrapper">
                <div class="offline-icon-circle">
                  <svg
                    class="offline-icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748B"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                    <line x1="12" y1="20" x2="12.01" y2="20"></line>
                  </svg>
                </div>
              </div>

              <h3 id="offline-title" class="offline-main-title">{{ offlineTitle }}</h3>
              <p id="offline-desc" class="offline-description">{{ offlineDescription }}</p>          
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { Capacitor } from '@capacitor/core'

export default {
  name: 'OfflineBanner',
  props: {
    offlineTitle: {
      type: String,
      default: 'You Are Offline'
    },
    offlineDescription: {
      type: String,
      default: "It looks like you're not connected to the internet. Please check your connection to view this page."
    }
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const isNative = computed(() => Capacitor.isNativePlatform())
    const isOnline = computed(() => store.state.network?.isOnline ?? true)
    const isDismissed = ref(false)
    let previousFocus = null
    let bounceTimer = null

    watch(
      () => route?.fullPath,
      () => {
        if (!isNative.value) return
        if (!isOnline.value) {
          if (bounceTimer) clearTimeout(bounceTimer)
          isDismissed.value = false
        }
      }
    )

    watch(isOnline, (online) => {
      if (!isNative.value) return
      if (!online) {
        if (bounceTimer) clearTimeout(bounceTimer)
        isDismissed.value = false
      }
    })

    const isVisible = computed(() => isNative.value && !isOnline.value && !isDismissed.value)

    // Accessibility focus management
    watch(isVisible, (visible) => {
      const appRoot = document.getElementById('app')
      if (visible) {
        if (appRoot) {
          appRoot.setAttribute('aria-hidden', 'true')
          if ('inert' in HTMLElement.prototype) {
            appRoot.inert = true
          }
        }
        previousFocus = document.activeElement
      } else {
        if (appRoot) {
          appRoot.removeAttribute('aria-hidden')
          if ('inert' in HTMLElement.prototype) {
            appRoot.inert = false
          }
        }
        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus()
        }
        previousFocus = null
      }
    })

    const dismiss = () => {
      isDismissed.value = true
      if (bounceTimer) clearTimeout(bounceTimer)

      bounceTimer = setTimeout(() => {
        if (isNative.value && !isOnline.value) {
          isDismissed.value = false
        }
      }, 300)
    }

    return {
      isNative,
      isOnline,
      isVisible,
      dismiss
    }
  }
}
</script>

<style scoped>
.offline-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 99999; 
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.offline-bottom-sheet {
  width: 100%;
  max-width: 480px;
  background-color: #ffffff;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Body */
.offline-sheet-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px 16px;
}

.offline-icon-wrapper {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.offline-icon-circle {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.offline-icon-svg {
  width: 38px;
  height: 38px;
}

.offline-main-title {
  margin: 0 0 10px;
  font-size: 1.1875rem;
  font-weight: 700;
  color: #0f172a;
}

.offline-description {
  margin: 0 0 24px;
  max-width: 320px;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #64748b;
}

.offline-continue-btn:active {
  transform: scale(0.98);
}

/* Transitions */
.offline-modal-fade-enter-active,
.offline-modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.offline-modal-fade-enter-from,
.offline-modal-fade-leave-to {
  opacity: 0;
}

.offline-sheet-slide-enter-active,
.offline-sheet-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.offline-sheet-slide-enter-from,
.offline-sheet-slide-leave-to {
  transform: translateY(100%);
}
</style>


